import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';

import { InputPositiveIntegerDirective } from '../shared/directives/input-positive-integer/input-positive-integer.directive';
import { RemovalsService } from './removals.service';
import { RemovalCode, RemovalReason } from './removal-options';
import { AppStringsPipe } from '../shared/pipes/appstrings.pipe';
import { TimeService } from '../shared/services/time.service';

interface RemovalRow {
  form: FormGroup<{
    date: FormControl<string>;
    pen: FormControl<string>;
    numberOfAnimals: FormControl<string>;
    weight: FormControl<string>;
    code: FormControl<string>;
    reason: FormControl<string>;
  }>;
}

@Component({
  selector: 'app-removals',
  templateUrl: './removals.component.html',
  imports: [ReactiveFormsModule, MatInputModule, MatOption, MatSelect, AppStringsPipe, InputPositiveIntegerDirective],
})
export class RemovalsComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly removalsService = inject(RemovalsService);
  private readonly timeService = inject(TimeService);
  protected readonly removalCodes = Object.values(RemovalCode);
  protected readonly removalReasons = Object.values(RemovalReason);
  private readonly SYSTEMATIC_CODE: string = RemovalCode.Systematic;
  private readonly removalRowsSignal = signal<RemovalRow[]>([]);
  protected readonly removalRows = this.removalRowsSignal.asReadonly();

  constructor() {
    //this.removalsService.getRemovals(); // No use (yet)
    this.addRow();
  }

  private addRow(): void {
    const row = this.createRemovalRow();
    row.form.valueChanges.subscribe(() => this.onRowChanged(row));
    this.removalRowsSignal.update((rows) => [...rows, row]);
  }

  private onRowChanged(row: RemovalRow): void {
    const rows = this.removalRowsSignal();
    const isLastRow = rows[rows.length - 1] === row;
    if (isLastRow && this.isRowFilled(row)) {
      this.addRow();
    }
  }

  private isRowFilled(row: RemovalRow): boolean {
    const { date, pen, numberOfAnimals, weight, code, reason } = row.form.getRawValue();
    const areRequiredFieldsFilled = date.trim() !== '' && pen.trim() !== '' && numberOfAnimals.trim() !== '' && weight.trim() !== '' && code.trim() !== '';
    if (!areRequiredFieldsFilled) {
      return false;
    }
    if (code !== this.SYSTEMATIC_CODE) {
      return reason.trim() !== '';
    }
    return true;
  }

  private createRemovalRow(): RemovalRow {
    return {
      form: this.formBuilder.nonNullable.group({
        date: this.timeService.getToday(),
        pen: '',
        numberOfAnimals: '',
        weight: '',
        code: '',
        reason: '',
      }),
    };
  }

  protected save(): void {
    const filledRows = this.removalRowsSignal().filter((row) => this.isRowFilled(row));
    this.removalsService.saveRemovals(filledRows.map((row) => row.form.getRawValue()));
  }
}